import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import AuthorizedAction from '../../components/AuthorizedAction.js';
import Empty from '../../components/Empty.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import ActivityFormModal from '../../components/projects/ActivityFormModal/index.jsx';
import { selectUser } from '../../modules/auth.js';
import {
  retrieveProject,
  retrieveProjects,
  selectProject,
  selectRetrieveProjectState,
  selectRetrieveProjectsState,
} from '../../modules/projects.js';
import EditMilestonesFormModal from '../../components/edit-milestones-form-modal/index.jsx';
import {
  retrieveChangeStrategy,
  selectChangeStrategy,
} from '../../modules/changeStrategy.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
  selectRetrieveStatusesState,
} from '../../modules/projectStatuses.js';
import {
  retrievePortfolio,
  retrievePortfolios,
  selectPortfolio,
} from '../../modules/portfolio.js';
import { deleteInitiative } from '../../modules/initiatives.js';
import ProjectHeader from '../../components/headers/PageHeaders/Project/ProjectHeader.jsx';
import { Helmet } from 'react-helmet-async';
import ProjectInitiatives from '../../components/projects/ProjectInitiatives/index.jsx';
import { isAuthorized } from '../../components/AuthorizedAction.js';
import { selectUserPermissions } from '../../modules/auth.js';

const mapStateToProps = (state, ownProps) => {
  const retrieveSuccess = [
    selectRetrieveProjectState,
    selectRetrieveStatusesState,
  ].every((select) => select(state).success);

  const project = selectProject(ownProps.projectId)(state);

  return {
    retrieveSuccess,
    project,
    changeStrategy: selectChangeStrategy(state),
    projectStatuses: selectProjectStatusesWithArchived(state),
    portfolio: project?.portfolioId
      ? selectPortfolio(project.portfolioId)(state)
      : null,
    user: selectUser(state),
    permissions: selectUserPermissions(state),
    projectsLoaded: selectRetrieveProjectsState(state).success,
  };
};
const mapDispatchToProps = {
  retrieveProject,
  retrieveProjects,
  retrieveChangeStrategy,
  deleteInitiative,
  retrieveProjectStatuses,
  retrievePortfolio,
  retrievePortfolios,
};

function ProjectInitiativesContainer({
  project,
  projectId,
  retrieveProject,
  portfolio,
  deleteInitiative,
  retrievePortfolios,
  user,
  changeStrategy,
  projectStatuses,
  retrieveChangeStrategy,
  retrieveProjectStatuses,
  permissions,
  projectsLoaded,
  retrieveProjects,
}) {
  const [formActivityId, setFormActivityId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [shownMilestones, setShownMilestones] = useState([]);
  const [showEditMilestonesFormModal, setShowEditMilestonesFormModal] =
    useState(false);

  const reloadProject = useCallback(
    () => retrieveProject(projectId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId]
  );

  const handleDeleteInitiative = (type, id) => {
    deleteInitiative(type, id).then(() => reloadProject());
  };

  // Fetch global data once on mount (not project-specific)
  useEffect(() => {
    retrieveProjectStatuses();
    retrievePortfolios();
    // Duplicate is offered when the user may create the initiative type in some project
    if (!projectsLoaded) retrieveProjects().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch project-specific data when projectId changes
  useEffect(() => {
    window.scrollTo(0, 0);

    Promise.all([
      retrieveProject(projectId),
      retrieveChangeStrategy(projectId),
    ]).catch((error) => {
      console.error('Error fetching project details:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const onAddActivity = useCallback(() => {
    setFormActivityId(null);
    setShowFormModal(true);
  }, []);

  const onClickActivity = useCallback(
    (activityId) => {
      if (user?.role === 'stakeholder') return;

      setFormActivityId(activityId);
      setShowFormModal(true);
    },
    [user]
  );

  const onHideModal = useCallback(() => {
    setShowFormModal(false);
  }, []);

  const onHideModalMilestones = useCallback(() => {
    setShowEditMilestonesFormModal(false);
  }, []);

  const onShowEditMilestone = (milestones) => {
    setShownMilestones(milestones);
    setShowEditMilestonesFormModal(true);
  };

  return (
    <>
      {!project || !project.communicationInitiatives || !project.activities ? (
        <Empty simplified content={loadingContent} />
      ) : (
        <>
          <Helmet>
            <title>{project.name} | ChangeSync</title>
          </Helmet>
          <ProjectHeader project={project} />
          <ProjectInitiatives
            onAddActivity={onAddActivity}
            onClickActivity={onClickActivity}
            project={project}
            portfolio={portfolio}
            changeStrategy={changeStrategy}
            projectStatuses={projectStatuses}
            deleteInitiative={handleDeleteInitiative}
            onShowEditMilestone={onShowEditMilestone}
          />
          <EditMilestonesFormModal
            onHide={onHideModalMilestones}
            show={showEditMilestonesFormModal}
            entities={shownMilestones}
            archived={project?.archived}
            canManage={project.manageProjectEntities}
            entityKey="changeStrategy"
            onSubmitSuccess={() => retrieveChangeStrategy(projectId)}
            onDeleteSuccess={() => retrieveChangeStrategy(projectId)}
          />
        </>
      )}

      <ActivityFormModal
        activityId={formActivityId}
        onHide={onHideModal}
        projectId={projectId}
        reloadProject={reloadProject}
        show={
          showFormModal &&
          !!project?.manageProjectEntities &&
          isAuthorized('update', 'activity', permissions)
        }
      />
    </>
  );
}

ProjectInitiativesContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectInitiativesContainer);
